import { useEffect, useState } from "react";
import { STATUS } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

export const StatusBadge = ({ s }) => {
  const key = s || "aguardando";
  return <Badge className={`status-${key} border`}>{STATUS[key] || key}</Badge>;
};

// Iter 98: badge colorido da rota (tabela de entregas, formulário e dashboard).
// Fundo = cor da rota com 10% de opacidade + borda/ponto na cor cheia; texto escuro
// pra manter legibilidade em cores claras (amarelo, lima…).
export function RotaBadge({ rota, testId, className = "" }) {
  if (!rota) return <span className="text-slate-300 text-xs" data-testid={testId}>—</span>;
  const cor = rota.cor || "#64748B";
  return (
    <span data-testid={testId} data-rota-id={rota.id} title={rota.ativa === false ? `${rota.nome} (rota inativa)` : rota.nome}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold text-slate-800 whitespace-nowrap max-w-[160px] ${className}`}
      style={{ backgroundColor: `${cor}1A`, borderColor: cor }}>
      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cor }} />
      <span className="truncate">{rota.nome}</span>
    </span>
  );
}

export const SEM_TROCO_FORMAS = ["já pago", "ja pago", "cartão", "cartao", "pix", "cheque", "crediário", "crediario", "convênio", "convenio"];

// fev/2026: máscara e normalização unificadas em `@/lib/phoneUtils`.
// Re-exportado aqui pra manter os imports antigos funcionando.
export { formatTelBR, normalizePhoneBR } from "@/lib/phoneUtils";

export const buildWhatsappUrl = (telefone, mensagem) => {
  const d = (telefone || "").replace(/\D/g, "");
  if (d.length < 10) return null;
  const withCountry = d.startsWith("55") ? d : ("55" + d);
  return `https://wa.me/${withCountry}?text=${encodeURIComponent(mensagem)}`;
};

export const DEFAULT_WPP_TEMPLATE = "Olá {cliente}! Sua entrega saiu para entrega às {hora}. Pedido {pedido}. Obrigado pela preferência! 🛵";

// Constantes fora do render para evitar recriação de objeto (menos re-renders no Combobox filho).
export const ENTREGADOR_NONE_OPT = { value: "__none__", label: "— Sem entregador (atribuir depois) —" };
export const FILTER_ENTREGADOR_OPTS = [
  { value: "all", label: "Todos" },
  { value: "__none__", label: "Sem entregador" },
];

export const isReceitaRevendaByName = (nome) => {
  const t = (nome || "").toLowerCase();
  return t.includes("receita") && t.includes("revenda");
};

// Monta string do pedido combinada (Rec+Rev) ou o pedido simples
export const buildPedidoLabel = (entrega, tipoNome, fallback = "—") => {
  if (isReceitaRevendaByName(tipoNome)) {
    return [entrega?.numero_pedido_receita ? `Rec ${entrega.numero_pedido_receita}` : null,
            entrega?.numero_pedido_revenda ? `Rev ${entrega.numero_pedido_revenda}` : null]
      .filter(Boolean).join(" · ") || fallback;
  }
  return entrega?.numero_pedido || fallback;
};

export const notifyEmTransito = (entrega, entregadores, empresa) => {
  if (!entrega.cliente_telefone) return null;
  const entregadorNome = entregadores.find((e) => e.id === entrega.entregador_id)?.nome || "";
  const hora = entrega.hr_saida
    ? new Date(entrega.hr_saida).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const template = (empresa?.whatsapp_msg_template || DEFAULT_WPP_TEMPLATE);
  const pedidoStr = (entrega.numero_pedido_receita || entrega.numero_pedido_revenda)
    ? [entrega.numero_pedido_receita ? `Rec ${entrega.numero_pedido_receita}` : null,
       entrega.numero_pedido_revenda ? `Rev ${entrega.numero_pedido_revenda}` : null]
      .filter(Boolean).join(" · ")
    : (entrega.numero_pedido || "");
  const msg = template
    .replaceAll("{cliente}", entrega.cliente_nome || "")
    .replaceAll("{pedido}", pedidoStr)
    .replaceAll("{entregador}", entregadorNome || "")
    .replaceAll("{hora}", hora)
    .replaceAll("{empresa}", empresa?.nome || "");
  return buildWhatsappUrl(entrega.cliente_telefone, msg);
};

// Componente com tick automático a cada 30s para mostrar tempo relativo em rota.
export function TempoEmRota({ hr_saida, status }) {
  const [tick, setTick] = useState(0);
  // Timer de UI puro — só precisa rodar uma vez no mount para gerar re-render periódico.
  // Não referencia props/estado externos além do setState local (estável).
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 30000); return () => clearInterval(t); }, []);
  if (status !== "em_transito" || !hr_saida) return null;
  const ms = Date.now() - new Date(hr_saida).getTime();
  if (ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  const hrs = Math.floor(mins / 60);
  const label = hrs > 0 ? `há ${hrs}h ${mins % 60}min` : `há ${mins} min`;
  const cls = mins >= 60 ? "text-rose-600" : mins >= 30 ? "text-amber-600" : "text-blue-600";
  // `tick` só força re-render (o valor não é exibido — `{tick && ""}` renderizava "0" no 1º tick).
  return <div className={`text-[10px] mt-0.5 font-medium ${cls}`} data-testid="tempo-em-rota" data-tick={tick}>{label} em rota</div>;
}

// Data local no formato YYYY-MM-DD
export const localDateStr = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
